import * as THREE from 'three';
import { MeshBVH, MeshBVHHelper } from 'three-mesh-bvh';
import type {Viewer} from '../Viewer';

/**
 * 草图圆弧端点调试盒类型。
 */
enum SketchArcEndpointDebugBoxKind {
    /** 圆弧起点调试盒。 */
    Start = 'start',
    /** 圆弧终点调试盒。 */
    End = 'end',
}

/**
 * 调试工具。
 * 职责：
 * 1. 统一创建调试辅助对象；
 * 2. 当前仅负责圆弧起点/终点调试盒创建；
 * 3. 保证调试对象统一添加到场景中。
 */
export class DebugTool {
    /** 圆弧端点调试盒边长。 */
    private static readonly ARC_ENDPOINT_DEBUG_BOX_SIZE = 0.008;
    /** Viewer 引用。 */
    private readonly viewer: Viewer;
    /** 特征结果体 BVH Helper 映射。 */
    private readonly featureBVHHelperMap: Map<string, { mesh: THREE.Mesh; helper: MeshBVHHelper }> = new Map();

    /**
     * 构造调试工具。
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 为特征结果体创建或刷新 BVH 与 BVH 调试 Helper。
     * 说明：
     * 1. 仅处理 FeatureResultMesh，避免基准面等辅助对象进入调试链路；
     * 2. 若已存在旧 Helper，会先释放后重建，避免调试对象重复；
     * 3. 本方法作为统一入口，由 Viewer 在结果体创建后调用。
     * @param object 目标对象。
     */
    public ensureFeatureResultMeshBVHAndHelper(object: THREE.Object3D): void {
        if (!(object instanceof THREE.Mesh)) {
            return;
        }
        if (Reflect.get(object, 'isFeatureResultMesh') !== true) {
            return;
        }

        const geometry = object.geometry as THREE.BufferGeometry & { boundsTree?: MeshBVH };
        if (!geometry) {
            return;
        }

        
        if (!geometry.boundsTree) {
            geometry.boundsTree = new MeshBVH(geometry);
        }
        

        this.removeFeatureResultMeshBVHHelper(object.uuid);

        
        const helper = new MeshBVHHelper(object, 8);
        helper.name = `DebugToolFeatureBVHHelper_${object.uuid}`;
        helper.renderOrder = 999;
        helper.visible = true;
        (helper as unknown as { ignore?: boolean }).ignore = true;
        this.applyHelperAlwaysVisibleStyle(helper);
        helper.update();

        this.viewer.scene.add(helper);
        this.featureBVHHelperMap.set(object.uuid, {
            mesh: object,
            helper,
        });
        
    }

    /**
     * 每帧刷新特征结果体 BVH Helper。
     * 说明：
     * 1. 强制保持 helper 可见，满足“始终显示”要求；
     * 2. 对象存在变换时，持续调用 update 同步显示；
     * 3. 若源网格已脱离场景，会自动清理对应 helper。
     */
    public syncFeatureResultMeshBVHHelpers(): void {
        const pendingRemoveUuids: string[] = [];

        this.featureBVHHelperMap.forEach((entry, objectUuid) => {
            const { mesh, helper } = entry;

            if (!mesh.parent) {
                pendingRemoveUuids.push(objectUuid);
                return;
            }

            helper.visible = true;
            helper.update();
        });

        pendingRemoveUuids.forEach((objectUuid) => {
            this.removeFeatureResultMeshBVHHelper(objectUuid);
        });
    }

    /**
     * 清理单个特征结果体的 BVH Helper。
     * @param objectUuid 特征结果体 UUID。
     */
    public removeFeatureResultMeshBVHHelper(objectUuid: string): void {
        const helperEntry = this.featureBVHHelperMap.get(objectUuid);
        if (!helperEntry) {
            return;
        }

        this.viewer.scene.remove(helperEntry.helper);
        this.disposeObject3DResources(helperEntry.helper);
        this.featureBVHHelperMap.delete(objectUuid);
    }

    /**
     * 清理全部特征结果体 BVH Helper。
     */
    public clearFeatureResultMeshBVHHelpers(): void {
        this.featureBVHHelperMap.forEach((entry) => {
            this.viewer.scene.remove(entry.helper);
            this.disposeObject3DResources(entry.helper);
        });
        this.featureBVHHelperMap.clear();
    }

    /**
     * 释放对象树中的几何与材质资源。
     * @param object 目标对象。
     */
    private disposeObject3DResources(object: THREE.Object3D): void {
        object.traverse((child) => {
            const objectWithGeometry = child as THREE.Object3D & { geometry?: THREE.BufferGeometry };
            const objectWithMaterial = child as THREE.Object3D & { material?: THREE.Material | THREE.Material[] };

            if (objectWithGeometry.geometry) {
                objectWithGeometry.geometry.dispose();
            }

            if (!objectWithMaterial.material) {
                return;
            }

            if (Array.isArray(objectWithMaterial.material)) {
                objectWithMaterial.material.forEach((materialItem) => materialItem.dispose());
                return;
            }

            objectWithMaterial.material.dispose();
        });
    }

    /**
     * 应用 BVH Helper 的常显样式。
     * @param helper BVH Helper 对象。
     */
    private applyHelperAlwaysVisibleStyle(helper: MeshBVHHelper): void {
        helper.traverse((child) => {
            const objectWithMaterial = child as THREE.Object3D & { material?: THREE.Material | THREE.Material[] };
            const material = objectWithMaterial.material;
            if (!material) {
                return;
            }

            const applySingleMaterial = (singleMaterial: THREE.Material): void => {
                singleMaterial.depthTest = false;
                singleMaterial.depthWrite = false;
                singleMaterial.transparent = true;
                singleMaterial.opacity = 0.95;
                singleMaterial.needsUpdate = true;
            };

            if (Array.isArray(material)) {
                material.forEach((materialItem) => applySingleMaterial(materialItem));
                return;
            }

            applySingleMaterial(material);
        });
    }

    /**
     * 为圆弧创建端点调试盒。
     * @param startWorld 圆弧起点世界坐标。
     * @param endWorld 圆弧终点世界坐标。
     */
    public createArcEndpointDebugBoxes(
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
    ): void {
        // 先创建圆弧起点调试盒，用绿色表示起点。
        this.createArcEndpointDebugBox(startWorld, SketchArcEndpointDebugBoxKind.Start);
        // 再创建圆弧终点调试盒，用红色表示终点。
        this.createArcEndpointDebugBox(endWorld, SketchArcEndpointDebugBoxKind.End);
    }

    /**
     * 创建单个圆弧端点调试盒并添加到场景。
     * @param worldPosition 调试盒对应的世界坐标。
     * @param kind 调试盒类型。
     */
    private createArcEndpointDebugBox(
        worldPosition: THREE.Vector3,
        kind: SketchArcEndpointDebugBoxKind,
    ): void {
        // 根据调试盒类型解析颜色与名称，便于调试识别。
        const debugBoxColor = kind === SketchArcEndpointDebugBoxKind.Start ? 0x22c55e : 0xef4444;
        const debugBoxName = kind === SketchArcEndpointDebugBoxKind.Start
            ? 'SketchArcStartDebugBox'
            : 'SketchArcEndDebugBox';

        // 创建立方体几何体和基础材质。
        const geometry = new THREE.BoxGeometry(
            DebugTool.ARC_ENDPOINT_DEBUG_BOX_SIZE,
            DebugTool.ARC_ENDPOINT_DEBUG_BOX_SIZE,
            DebugTool.ARC_ENDPOINT_DEBUG_BOX_SIZE,
        );
        const material = new THREE.MeshBasicMaterial({
            color: debugBoxColor,
            transparent: true,
            opacity: 0.85,
            depthTest: false,
            depthWrite: false,
        });

        // 创建网格对象并设置调试渲染优先级。
        const debugBox = new THREE.Mesh(geometry, material);
        (debugBox as any).ignore = true;
        debugBox.name = debugBoxName;
        debugBox.renderOrder = 999;

        // 直接写入世界坐标并添加到场景，保证所有调试对象统一挂载到 scene。
        debugBox.position.copy(worldPosition);

        // 将调试盒添加到 Viewer 场景中。
        this.viewer.addObject(debugBox);
    }
}
